import React, { useEffect, useState } from 'react';
import { useTranslation } from '@shared/core';
import { Box, ButtonV2, DialogV2, TextV2, Flex, DrawerV2, Divider } from '@withjoy/joykit';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { FormDialogWrapper } from './Form';
import { Wrapper } from './components';
import { Selector } from './components/Selector/Selector';
import { FormikProps } from 'formik';
import { AddressDialogFields } from '@apps/registry/common/components/AddressDialog/types';
import { useAddressVerificationController } from './ShippingAddressVerification.controller';
import { useFormController } from './Form.controller';
import { pxToRem } from '@withjoy/joykit/theme';
import { useMount } from '@shared/utils/hooks/useMount';
import { DIALOG_OVERRIDES, DRAWER_OVERRIDES } from './ShippingAddress.style';
import { useShippingAddressTelemetry } from './ShippingAddress.telemetry';
import { TempSpinner } from '@shared/components/TempSpinner';
import { ReactComponent as Map } from '@apps/registry/admin/assets/map.svg';

interface ShippingAddressReminderDialogProps {
  eventId: string;
  isOpen: boolean;
  onClose: () => void;

  defaultView?: ShippingDialogView;
  eventHandle: string;
  title?: string;
  subtitle?: string;
  disableUserClose?: boolean;
  pageName?: string;
}

export type ShippingDialogView = 'reminder' | 'form' | 'verify';

export const ShippingAddressReminderDialog: React.FC<ShippingAddressReminderDialogProps> = ({
  eventId,
  eventHandle,
  isOpen,
  onClose,
  defaultView,
  disableUserClose,
  title,
  subtitle,
  pageName
}) => {
  const [view, setView] = useState<ShippingDialogView>('form');

  const formController = useFormController({ eventId: eventId as string, eventHandle, setView, onCloseDialog: onClose });
  const telemetry = useShippingAddressTelemetry();
  useEffect(() => {
    if (isOpen) {
      const telemetryEvent = view === 'form' || view === 'verify' ? telemetry.shippingAddressFormDialog.enter : telemetry.reminderAddShippingAddressDialog.enter;

      telemetryEvent({ pageNameLocation: pageName });
    }
  }, [isOpen, telemetry, view, pageName]);

  useMount(() => {
    if (defaultView && defaultView !== view) {
      setView(defaultView);
    }
  });

  const trackAndCloseDialog = () => {
    telemetry.closeAddressDialog();
    onClose();
  };

  return (
    <>
      {view === 'form' ? (
        <FormDialogWrapper disableUserClose={disableUserClose} isOpen={isOpen} onClose={trackAndCloseDialog} view={view} {...formController} title={title} subtitle={subtitle} />
      ) : (
        <ShippingAddressWrapper isOpen={isOpen} onClose={trackAndCloseDialog} disableUserClose={disableUserClose} view={view}>
          {view === 'reminder' && <ShippingAddressReminderBody setView={setView} onCloseReminder={trackAndCloseDialog} />}
          {view === 'verify' && <VerifyAddress setView={setView} formik={formController.formik} />}
        </ShippingAddressWrapper>
      )}
    </>
  );
};

const ShippingAddressWrapper: React.FC<{ isOpen: boolean; onClose: () => void; disableUserClose?: boolean; view: ShippingDialogView }> = ({
  isOpen,
  onClose,
  children,
  disableUserClose = false,
  view
}) => {
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const { t2 } = useTranslation('sharedComponents');
  const { shippingAdressFormTitle, addressSuggestion } = t2('shippingAddressDialog');

  const getTitle = () => {
    switch (view) {
      case 'form':
        return shippingAdressFormTitle;
      case 'reminder':
        return '';
      case 'verify':
        return addressSuggestion;
    }
  };

  const getSize = () => {
    switch (view) {
      case 'form':
        return 'xl';
      case 'reminder':
        return 'lg';
      case 'verify':
        return 'xl';
      default:
        return 'xl';
    }
  };

  return !isMobile ? (
    <DialogV2
      size={getSize()}
      isOpen={isOpen}
      overrides={DIALOG_OVERRIDES}
      onClose={onClose}
      disableCloseOnEscapeKeyClick={disableUserClose}
      disableCloseOnOutsideClick={disableUserClose}
      stickyBehavior="header-footer"
    >
      <DialogV2.Header>
        <TextV2 typographyVariant="hed2">{getTitle()}</TextV2>
        {!disableUserClose && <DialogV2.CloseButton />}
      </DialogV2.Header>
      <DialogV2.Body>{children}</DialogV2.Body>
    </DialogV2>
  ) : (
    <DrawerV2
      onClose={onClose}
      disableAutoFocus
      size="auto"
      overrides={DRAWER_OVERRIDES}
      isOpen={isOpen}
      anchor="bottom"
      useBackdrop
      disableCloseOnEscapeKeyClick={disableUserClose}
      disableCloseOnOutsideClick={disableUserClose}
    >
      <DrawerV2.Header>
        <TextV2 typographyVariant="hed2">{getTitle()}</TextV2>
        {!disableUserClose && <DrawerV2.CloseButton />}
      </DrawerV2.Header>
      <DrawerV2.Body>{children}</DrawerV2.Body>
    </DrawerV2>
  );
};

const ShippingAddressReminderBody: React.FC<{ setView: (value: ShippingDialogView) => void; onCloseReminder: () => void }> = ({ setView, onCloseReminder }) => {
  const { t2 } = useTranslation('sharedComponents');
  const { reminderTitle, reminderDescription, addShippingAddressBtn, remindMeLater } = t2('shippingAddressDialog');
  return (
    <Box padding={6}>
      <Flex>
        <TextV2 textAlign="center" typographyVariant="hed6" color="mono14">
          {reminderTitle}
        </TextV2>
      </Flex>
      <Box>
        <TextV2 marginTop={6} marginBottom={6} typographyVariant="body2" color="mono14">
          {reminderDescription}
        </TextV2>
      </Box>
      <Flex justifyContent="center" marginY={7}>
        <Map />
      </Flex>
      <Box alignItems="center">
        <ButtonV2 intent="neutral" fullWidth={true} onClick={() => setView('form')}>
          {addShippingAddressBtn}
        </ButtonV2>
        <ButtonV2 intent="neutral" variant="ghost" fullWidth={true} onClick={onCloseReminder}>
          {remindMeLater}
        </ButtonV2>
      </Box>
    </Box>
  );
};

const VerifyAddress: React.FC<{ formik: FormikProps<AddressDialogFields>; setView: (value: ShippingDialogView) => void }> = ({ formik, setView }) => {
  const { suggestedAddress, addressOption, setAddressOption, handleOnConfirmAddressClick, isValidatingAddress } = useAddressVerificationController(formik);
  const { t2 } = useTranslation('sharedComponents');
  const { addressSuggestionDescription, confirmAddress, back } = t2('shippingAddressDialog');
  if (isValidatingAddress) {
    return (
      <Flex justifyContent="center" alignItems="center" height={pxToRem(300)}>
        <TempSpinner />
      </Flex>
    );
  }
  return (
    <>
      <Divider />
      <Wrapper bigger={true}>
        <Flex flexDirection="column" alignItems="center" justifyContent="center" paddingX={['24px', null, 0]} width="100%">
          <TextV2 typographyVariant="body1" alignSelf="start" width="100%" marginBottom={6}>
            {addressSuggestionDescription}
          </TextV2>
          <Selector formik={formik} suggestedAddress={suggestedAddress} addressOption={addressOption} setAddressOption={setAddressOption} />
          <Flex justifyContent="flex-end" marginTop="16px" width="100%">
            <ButtonV2 variant="ghost" onClick={() => setView('form')} intent="neutral">
              {back}
            </ButtonV2>
            <ButtonV2 onClick={handleOnConfirmAddressClick} intent="neutral">
              {confirmAddress}
            </ButtonV2>
          </Flex>
        </Flex>
      </Wrapper>
    </>
  );
};
